import { memo, useCallback, useRef, useState } from "react";
import {
  Calendar,
  Loader2,
  MapPin,
  Search,
} from "lucide-react";
import type { Event, SearchResult } from "@/lib/api";
import { getEventColor, getEventLabel } from "@/lib/api";
import { cn } from "@trotroways/utils";
import { useLocationSearch } from "@/hooks/use-location-search";

export interface ExploreSearchPanelProps {
  events: Event[];
  onSelectLocation: (result: SearchResult) => void;
  onSelectEvent: (event: Event) => void;
}

export const ExploreSearchPanel = memo(function ExploreSearchPanel({
  events,
  onSelectLocation,
  onSelectEvent,
}: ExploreSearchPanelProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    results: locationResults,
    setResults: setLocationResults,
    isSearching,
  } = useLocationSearch(query);

  const matchingEvents = query.length >= 2
    ? events.filter(
      (e) =>
        e.title.toLowerCase().includes(query.toLowerCase()) ||
        e.venue_name?.toLowerCase().includes(query.toLowerCase()),
    )
    : [];

  const handleSelectLocation = useCallback(
    (result: SearchResult) => {
      setQuery(result.name);
      setLocationResults([]);
      onSelectLocation(result);
    },
    [onSelectLocation, setLocationResults],
  );

  const handleSelectEvent = useCallback(
    (event: Event) => {
      setQuery(event.title);
      onSelectEvent(event);
    },
    [onSelectEvent],
  );

  const hasResults = matchingEvents.length > 0 || locationResults.length > 0;
  const showResults = query.length >= 2;

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search places, events..."
          className={cn(
            "w-full rounded-xl border border-zinc-200/80 bg-white/60 py-2.5 pl-10 pr-10 text-sm",
            "outline-none placeholder:text-zinc-400",
            "transition-colors focus:border-zinc-300 focus:bg-white/80",
          )}
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 animate-spin text-zinc-400" />
        )}
      </div>

      {showResults && (
        <div className="mt-2 max-h-64 overflow-y-auto scrollbar-hide">
          {matchingEvents.length > 0 && (
            <div className="mb-1">
              <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                Events
              </p>
              {matchingEvents.slice(0, 5).map((event) => (
                <button
                  type="button"
                  key={event.id}
                  onClick={() => handleSelectEvent(event)}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-zinc-100/80"
                >
                  <div
                    className="flex size-7 shrink-0 items-center justify-center rounded-lg"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${getEventColor(event.event_type)} 15%, transparent)`,
                    }}
                  >
                    <Calendar
                      className="size-3.5"
                      style={{ color: getEventColor(event.event_type) }}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-zinc-800">
                      {event.title}
                    </p>
                    <p className="truncate text-xs text-zinc-400">
                      {getEventLabel(event.event_type)}
                      {event.venue_name && ` · ${event.venue_name}`}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {locationResults.length > 0 && (
            <div>
              {matchingEvents.length > 0 && (
                <div className="mx-2 my-1 border-t border-zinc-100" />
              )}
              <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                Places
              </p>
              {locationResults.map((result) => (
                <button
                  type="button"
                  key={`${result.name}-${result.latitude}-${result.longitude}`}
                  onClick={() => handleSelectLocation(result)}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-zinc-100/80"
                >
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-zinc-100">
                    <MapPin className="size-3.5 text-zinc-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-zinc-800">
                      {result.name}
                    </p>
                    {result.address && (
                      <p className="truncate text-xs text-zinc-400">
                        {result.address}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {!hasResults && !isSearching && (
            <p className="py-6 text-center text-xs text-zinc-400">
              No results found
            </p>
          )}
        </div>
      )}
    </div>
  );
});
